<script setup>
import {
    ref,
    getCurrentInstance
} from 'vue'
import {
    DriverStore
} from '../pinia/Driver.js'

const Driver = DriverStore()
const {
    proxy
} = getCurrentInstance()
const pseudo = ref("" + Driver.persona.name)
const snackbar = ref(false)
const error = ref('')
const avatar = ref(Driver.persona.iconIndex)
const color = ref(Driver.user.nicknameColor)

async function save() {
    error.value = ''
    let saved = false

    if (pseudo.value != Driver.persona.name) {
        try {
            await proxy.$http.post("", JSON.stringify({
                serviceName: "account",
                methodName: "UpdatePersonaName",
                parameters: [pseudo.value]
            }))
            Driver.persona.name = pseudo.value
            saved = true
        } catch (err) {
            error.value = err.response?.data?.error || 'Error updating name'
            return
        }
    }

    if (avatar.value != Driver.persona.iconIndex) {
        try {
            await proxy.$http.post("", JSON.stringify({
                serviceName: "account",
                methodName: "avatar",
                parameters: [avatar.value]
            }))
            Driver.persona.iconIndex = avatar.value
            saved = true
        } catch (err) {
            error.value = err.response?.data?.error || 'Error updating avatar'
            return
        }
    }

    if (color.value != Driver.user.nicknameColor) {
        try {
            await proxy.$http.post("", JSON.stringify({
                serviceName: "account",
                methodName: "UpdateColor",
                parameters: [color.value]
            }))
            Driver.user.nicknameColor = color.value
            saved = true
        } catch (err) {
            error.value = err.response?.data?.error || 'Error updating color'
            return
        }
    }

    if (saved) {
        snackbar.value = true
    }
}
</script>

<template>
<div class="personaForm">
    <v-snackbar :timeout="2000" color="deep-purple-accent-4" elevation="24" v-model="snackbar">
        Your driver informations have been saved.
    </v-snackbar>

    <v-alert class="error" v-if="error" :text="error" type="error"></v-alert>

    <v-form>
        <v-container>
            <!-- Premium Warning -->
            <v-alert 
                v-if="Driver.user.premium != '1'" 
                color="info" 
                icon="mdi-information"
                class="mb-4"
            >
                You have to be a Premium member to edit your driver.
            </v-alert>

            <!-- Driver Settings Card -->
            <div class="settings-card">
                <div class="settings-card-header">
                    <div class="header-icon">
                        <v-icon size="28">mdi-account-edit</v-icon>
                    </div>
                    <div class="header-content">
                        <h3 class="settings-title">Driver Profile</h3>
                        <p class="settings-subtitle">Customize your driver appearance</p>
                    </div>
                </div>

                <div class="settings-card-body">
                    <!-- Driver Name -->
                    <div class="setting-item">
                        <div class="setting-info full-width">
                            <div class="setting-label">
                                <v-icon size="20" class="mr-2">mdi-account</v-icon>
                                Driver Name
                            </div>
                            <div class="setting-description">
                                Your unique driver name (3-14 characters, alphanumeric)
                            </div>
                            <v-text-field 
                                v-model="pseudo" 
                                placeholder="Driver Name" 
                                variant="outlined"
                                density="comfortable"
                                hide-details
                                :disabled="Driver.user.premium != '1'"
                                class="setting-input"
                            ></v-text-field>
                        </div>
                    </div>

                    <v-divider class="my-4"></v-divider>

                    <!-- Nickname Color -->
                    <div class="setting-item">
                        <div class="setting-info">
                            <div class="setting-label">
                                <v-icon size="20" class="mr-2">mdi-palette</v-icon>
                                Nickname Color
                            </div>
                            <div class="setting-description">
                                Choose a custom color for your driver name
                            </div>
                        </div>
                        <div class="color-picker-wrapper">
                            <input 
                                type='color' 
                                class="color-input" 
                                v-model="color" 
                                :disabled="Driver.user.premium != '1'"
                            >
                        </div>
                    </div>

                    <v-divider class="my-4"></v-divider>

                    <!-- Avatar Selection -->
                    <div class="setting-item">
                        <div class="setting-info full-width">
                            <div class="setting-label">
                                <v-icon size="20" class="mr-2">mdi-face-man</v-icon>
                                Avatar
                            </div>
                            <div class="setting-description">
                                Choose your driver avatar
                            </div>
                            
                            <div class="avatar-grid">
                                <!-- Avatar avec l'ID 0 -->
                                <span class="avatar-item" :class="{'avatar-selected': parseInt(Driver.persona.iconIndex) === 0}">
                                    <label :for="'label0'">
                                        <img 
                                            :src="'https://cdn.nightriderz.world/images/website/icon-persona/0.jpg'" 
                                            width="60" 
                                            height="60"
                                        >
                                    </label>
                                    <input 
                                        type="radio" 
                                        id="label0" 
                                        name="avatar" 
                                        @click="avatar = 0" 
                                        :checked="parseInt(Driver.persona.iconIndex) === 0" 
                                        :disabled="Driver.user.premium != '1'"
                                    >
                                </span>

                                <!-- Boucle pour les autres avatars -->
                                <span 
                                    v-for="i in 72" 
                                    :key="i"
                                    class="avatar-item" 
                                    :class="{'item26': i === 26, 'avatar-selected': parseInt(Driver.persona.iconIndex) === i}"
                                >
                                    <label :for="'label' + i">
                                        <img 
                                            v-if="i !== 26" 
                                            :src="'https://cdn.nightriderz.world/images/website/icon-persona/' + i + '.jpg'" 
                                            width="60" 
                                            height="60"
                                        >
                                    </label>
                                    <input 
                                        type="radio" 
                                        :id="'label' + i" 
                                        name="avatar" 
                                        @click="avatar = i" 
                                        :checked="parseInt(Driver.persona.iconIndex) === i" 
                                        :disabled="Driver.user.premium != '1'"
                                    >
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Save Button -->
            <v-btn 
                v-if="Driver.user.premium == '1'"
                class="save-btn" 
                variant="elevated" 
                color="primary"
                size="large"
                @click="save"
                block
            >
                <v-icon class="mr-2">mdi-content-save</v-icon>
                Save Changes
            </v-btn>
        </v-container>
    </v-form>
</div>
</template>

<style scoped>
.personaForm {
    margin: 0;
    padding: 0;
}

.personaForm .v-icon {
    color: currentColor;
    opacity: 1;
}

.settings-card {
    background: rgba(255, 255, 255, 0.03);
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    overflow: hidden;
    margin-bottom: 20px;
}

.settings-card-header {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px;
    background: rgba(var(--v-theme-primary), 0.1);
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.header-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background: rgba(var(--v-theme-primary), 0.2);
    border-radius: 10px;
}

.header-icon .v-icon {
    color: rgb(var(--v-theme-primary));
}

.header-content {
    flex: 1;
}

.settings-title {
    margin: 0;
    font-size: 1.3em;
    font-weight: 600;
    color: rgb(var(--v-theme-primary));
}

.settings-subtitle {
    margin: 4px 0 0 0;
    font-size: 0.85em;
    color: rgba(255, 255, 255, 0.6);
}

.settings-card-body {
    padding: 24px;
}

.setting-item {
    display: flex;
    align-items: flex-start;
    gap: 16px;
}

.setting-info {
    flex: 1;
}

.setting-info.full-width {
    width: 100%;
}

.setting-label {
    display: flex;
    align-items: center;
    font-size: 1em;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 6px;
}

.setting-label .v-icon {
    color: rgba(255, 255, 255, 0.9);
}

.setting-description {
    font-size: 0.85em;
    color: rgba(255, 255, 255, 0.5);
    margin-bottom: 16px;
}

.setting-input {
    margin-top: 0;
}

.color-picker-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
}

.color-input {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 2px solid rgba(var(--v-theme-primary), 0.3);
    cursor: pointer;
    background: transparent;
    transition: all 0.3s ease;
}

.color-input:hover:not(:disabled) {
    border-color: rgba(var(--v-theme-primary), 0.6);
    transform: scale(1.05);
}

.color-input:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.color-input::-webkit-color-swatch-wrapper {
    padding: 0;
    border-radius: 50%;
}

.color-input::-webkit-color-swatch {
    border: none;
    border-radius: 50%;
}

.avatar-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    max-height: 300px;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 8px;
    background: rgba(255, 255, 255, 0.02);
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.05);
}

.avatar-grid::-webkit-scrollbar {
    width: 6px;
}

.avatar-grid::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.02);
    border-radius: 3px;
}

.avatar-grid::-webkit-scrollbar-thumb {
    background: rgba(var(--v-theme-primary), 0.3);
    border-radius: 3px;
}

.avatar-grid::-webkit-scrollbar-thumb:hover {
    background: rgba(var(--v-theme-primary), 0.5);
}

.avatar-item {
    position: relative;
    cursor: pointer;
}

.avatar-item.item26 {
    display: none;
}

.avatar-item img {
    border-radius: 8px;
    border: 2px solid rgba(255, 255, 255, 0.1);
    transition: all 0.2s ease;
    cursor: pointer;
}

.avatar-item:hover:not(:has(input:disabled)) img {
    border-color: rgba(var(--v-theme-primary), 0.5);
    transform: scale(1.05);
}

.avatar-item.avatar-selected img {
    border-color: rgb(var(--v-theme-primary));
    box-shadow: 0 0 12px rgba(var(--v-theme-primary), 0.5);
}

.avatar-item input[type="radio"] {
    position: absolute;
    left: 4px;
    top: 4px;
    accent-color: rgb(var(--v-theme-primary));
    cursor: pointer;
}

.avatar-item input[type="radio"]:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.save-btn {
    margin-top: 8px;
    font-weight: 600;
    letter-spacing: 0.5px;
}

.save-btn .v-icon {
    opacity: 1 !important;
}

.error {
    margin-bottom: 20px;
}
</style>

<style>
.v-input__details {
    padding-inline-start: 2px !important;
}
</style>
